import { apiClient } from '../config';
import { endpoints } from '../endpoints';

/**
 * Location service for handling country and state operations
 */
export const locationService = {
  /**
   * Get all countries
   * @returns {Promise<Array>} - Promise resolving to array of countries
   */
  getAllCountries: async () => {
    const response = await apiClient.get(endpoints.locations.countries.getAll);
    return response.data;
  },

  /**
   * Get country by ID
   * @param {string} id - Country ID
   * @returns {Promise<Object>} - Promise resolving to country data
   */
  getCountryById: async (id) => {
    const response = await apiClient.get(`${endpoints.locations.countries.getById(id)}`);
    return response.data;
  },

  /**
   * Create a new country
   * @param {Object} countryData - Country data
   * @param {string} countryData.name - Country name
   * @param {string} countryData.code - Country code (ISO 3166-1 alpha-2)
   * @returns {Promise<Object>} - Promise resolving to created country
   */
  createCountry: async (countryData) => {
    const response = await apiClient.post(endpoints.locations.countries.create, countryData);
    return response.data;
  },

  /**
   * Update an existing country
   * @param {string} id - Country ID
   * @param {Object} countryData - Country data to update
   * @param {string} [countryData.name] - Country name
   * @param {string} [countryData.code] - Country code
   * @returns {Promise<Object>} - Promise resolving to updated country
   */
  updateCountry: async (id, countryData) => {
    const response = await apiClient.put(`${endpoints.locations.countries.update(id)}`, countryData);
    return response.data;
  },

  /**
   * Delete a country
   * @param {string} id - Country ID
   * @returns {Promise<Object>} - Promise resolving to success message
   */
  deleteCountry: async (id) => {
    const response = await apiClient.delete(`${endpoints.locations.countries.delete(id)}`);
    return response.data;
  },

  /**
   * Get all states
   * @param {string} [countryId] - Optional country ID to filter states
   * @returns {Promise<Array>} - Promise resolving to array of states
   */
  getAllStates: async (countryId) => {
    const url = countryId 
      ? `${endpoints.locations.states.getAll}?countryId=${countryId}` 
      : endpoints.locations.states.getAll;
    const response = await apiClient.get(url);
    return response.data;
  },

  /**
   * Get state by ID
   * @param {string} id - State ID
   * @returns {Promise<Object>} - Promise resolving to state data
   */
  getStateById: async (id) => {
    const response = await apiClient.get(`${endpoints.locations.states.getById(id)}`);
    return response.data;
  },

  /**
   * Create a new state
   * @param {Object} stateData - State data
   * @param {string} stateData.name - State name
   * @param {string} stateData.code - State code
   * @param {string} stateData.countryId - Country ID
   * @returns {Promise<Object>} - Promise resolving to created state
   */
  createState: async (stateData) => {
    const response = await apiClient.post(endpoints.locations.states.create, stateData);
    return response.data;
  },

  /**
   * Update an existing state
   * @param {string} id - State ID
   * @param {Object} stateData - State data to update
   * @param {string} [stateData.name] - State name
   * @param {string} [stateData.code] - State code
   * @param {string} [stateData.countryId] - Country ID
   * @returns {Promise<Object>} - Promise resolving to updated state
   */
  updateState: async (id, stateData) => {
    const response = await apiClient.put(`${endpoints.locations.states.update(id)}`, stateData);
    return response.data;
  },

  /**
   * Delete a state
   * @param {string} id - State ID
   * @returns {Promise<Object>} - Promise resolving to success message
   */
  deleteState: async (id) => {
    const response = await apiClient.delete(`${endpoints.locations.states.delete(id)}`);
    return response.data;
  }
};
